import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileUp, Loader2, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;
const currentMonth = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
};
const shift = (month, delta) => {
  const m = parseInt(month.slice(0, 2), 10), y = parseInt(month.slice(2), 10);
  const d = new Date(y, m - 1 + delta, 1);
  return `${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
};

const SECTIONS = [
  ["salaries", "Neto plaće (HR69)", "subtotal_salaries"],
  ["taxes", "Porezi i doprinosi", "subtotal_taxes"],
  ["leases", "Najam vozila (HR02)", "subtotal_leases"],
];

export default function AdminSepa() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/sepa/preview", { params: { month } })
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err?.response?.status === 401) navigate("/admin");
        else toast.error("Loading failed.");
      })
      .finally(() => setLoading(false));
  }, [month, navigate]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("month", month);
      fd.append("file", file);
      const { data } = await api.post("/admin/sepa/accountant-pdf", fd, { timeout: 180_000 });
      toast.success(`OCR parsed ${data.parsed} payment orders.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload/OCR failed.");
    } finally {
      setUploading(false);
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/admin/sepa/items/${id}`);
      load();
    } catch { toast.error("Delete failed."); }
  };

  const downloadXml = async () => {
    try {
      const resp = await api.get("/admin/sepa/xml", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = "SEPA_JUKE_TRANSFERS.xml";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      const detail = err?.response?.data instanceof Blob ? null : err?.response?.data?.detail;
      toast.error(detail || "XML generation failed.");
    }
  };

  const total = data?.grand_total || 0;
  const txCount = data ? data.salaries.length + data.taxes.length + data.leases.length : 0;

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Toaster position="top-right" theme="dark" />
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/admin/dashboard")} data-testid="sepa-back"
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Juke Transfers j.d.o.o.</p>
            <h1 className="text-lg text-white font-medium">SEPA Isplate — Zero-Touch Batch</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setMonth((m) => shift(m, -1))} data-testid="sepa-month-prev"
              className="px-3 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-sm">←</button>
            <span className="text-base tabular-nums px-2" data-testid="sepa-month-label">
              {month.slice(0, 2)}/{month.slice(2)}
            </span>
            <button onClick={() => setMonth((m) => shift(m, 1))} data-testid="sepa-month-next"
              className="px-3 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-sm">→</button>
            <button onClick={load} className="p-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-400"
              data-testid="sepa-refresh"><RefreshCw className="w-4 h-4" /></button>
          </div>
          <div className="flex-1" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sepa-upload-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/10 text-sm disabled:opacity-60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
            {uploading ? "OCR u tijeku…" : "Učitaj PDF knjigovođe"}
          </button>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden"
            onChange={upload} data-testid="sepa-file-input" />
          <button onClick={downloadXml} disabled={!txCount} data-testid="sepa-download-xml"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold text-sm disabled:opacity-50">
            <Download className="w-4 h-4" /> SEPA_JUKE_TRANSFERS.xml
          </button>
        </div>

        {data?.missing_iban?.length > 0 && (
          <p className="text-xs text-amber-300 border border-amber-400/30 bg-amber-400/5 rounded-xl px-4 py-3"
            data-testid="sepa-missing-iban">
            ⚠ Nedostaje IBAN za: {data.missing_iban.join(", ")} — ti nalozi neće ući u XML.
          </p>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500 py-12"><Loader2 className="w-4 h-4 animate-spin" /> Učitavanje…</div>
        ) : (
          <>
            {SECTIONS.map(([key, title, subKey]) => (
              <section key={key} className="rounded-2xl border border-white/10 bg-[#0d0d0d] overflow-hidden"
                data-testid={`sepa-section-${key}`}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                  <h2 className="text-sm uppercase tracking-[0.2em] text-[#C5A059]">{title}</h2>
                  <span className="text-sm tabular-nums text-white" data-testid={`sepa-subtotal-${key}`}>
                    Međuzbroj: {eur(data?.[subKey])}
                  </span>
                </div>
                {!(data?.[key] || []).length ? (
                  <p className="px-5 py-4 text-sm text-zinc-600">Nema naloga.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-zinc-500 uppercase tracking-wider text-[10px]">
                          <th className="text-left px-5 py-2">Primatelj</th>
                          <th className="text-left px-3 py-2">IBAN</th>
                          <th className="text-left px-3 py-2">Model</th>
                          <th className="text-left px-3 py-2">Poziv na broj</th>
                          <th className="text-left px-3 py-2">Opis</th>
                          <th className="text-right px-3 py-2">Iznos</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {data[key].map((r) => (
                          <tr key={r.id} className="border-t border-white/5">
                            <td className="px-5 py-2.5 text-white">{r.recipient_name}</td>
                            <td className="px-3 py-2.5 font-mono text-[11px]">{r.iban || <span className="text-amber-300">NEDOSTAJE</span>}</td>
                            <td className="px-3 py-2.5">{r.model || "—"}</td>
                            <td className="px-3 py-2.5 font-mono text-[11px]">{r.reference || "—"}</td>
                            <td className="px-3 py-2.5 text-zinc-400 max-w-[220px] truncate">{r.description || "—"}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-white">{eur(r.amount_eur)}</td>
                            <td className="px-3 py-2.5 text-right">
                              {r.source === "accountant_pdf" && (
                                <button onClick={() => removeItem(r.id)} data-testid={`sepa-delete-${r.id}`}
                                  className="p-1.5 rounded-full hover:bg-red-500/10 text-zinc-600 hover:text-red-400">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}

            <div className="flex items-center justify-between rounded-2xl border border-[#C5A059]/40 bg-[#C5A059]/5 px-6 py-4">
              <span className="text-sm uppercase tracking-[0.25em] text-[#C5A059]">Ukupno · {txCount} naloga</span>
              <span className="text-2xl font-display text-[#C5A059] tabular-nums" data-testid="sepa-grand-total">{eur(total)}</span>
            </div>

            {data?.markdown && (
              <details className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-5" data-testid="sepa-markdown">
                <summary className="text-xs uppercase tracking-[0.2em] text-zinc-400 cursor-pointer">Markdown pregled</summary>
                <pre className="mt-3 text-[11px] text-zinc-300 whitespace-pre-wrap overflow-x-auto">{data.markdown}</pre>
              </details>
            )}
          </>
        )}
      </main>
    </div>
  );
}
